import {
  Title,
  Stack,
  Text,
  Badge,
  Button,
  Alert,
  Group,
  Loader,
  Center,
  Paper,
  ActionIcon,
  TextInput,
  Modal,
  Accordion,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconSeeding,
  IconPlus,
  IconPencil,
  IconTrash,
  IconToggleLeft,
  IconToggleRight,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { adminService } from '../../services/admin';
import { CATEGORY_EMOJI } from '../../utils/categories';
import { EmojiPickerPopover } from '../../components/EmojiPickerPopover';
import type { AdminCategory, AdminCategoryBase } from '../../types';

export default function AdminCategories() {
  const queryClient = useQueryClient();
  const [addModal, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [editModal, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmoji, setEditEmoji] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<number | undefined>();
  const [selectedCategory, setSelectedCategory] = useState<AdminCategory | AdminCategoryBase | null>(null);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => adminService.getCategories(),
    staleTime: 60_000,
  });

  const seedMutation = useMutation({
    mutationFn: () => adminService.seedCategories(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-categories'] }),
  });

  const createMutation = useMutation({
    mutationFn: () => adminService.createCategory(newName.trim(), selectedParentId, newEmoji.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      setNewName('');
      setNewEmoji('');
      closeAdd();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => adminService.updateCategory(selectedCategory!.id, editName.trim(), editEmoji.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      closeEdit();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => adminService.toggleCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-categories'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-categories'] }),
  });

  const openAddModal = (parentId?: number) => {
    setSelectedParentId(parentId);
    setNewName('');
    setNewEmoji('');
    openAdd();
  };

  const openEditModal = (cat: AdminCategory | AdminCategoryBase) => {
    setSelectedCategory(cat);
    setEditName(cat.name);
    setEditEmoji(cat.emoji ?? '');
    openEdit();
  };

  const totalCount = categories.length + categories.reduce((sum, c) => sum + c.subcategories.length, 0);

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Categories</Title>
        <Group gap="sm">
          <Badge size="lg" variant="light">{totalCount} total</Badge>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => openAddModal()}
            size="sm"
          >
            Add Category
          </Button>
          <Button
            leftSection={<IconSeeding size={16} />}
            loading={seedMutation.isPending}
            onClick={() => seedMutation.mutate()}
            size="sm"
            variant="light"
          >
            Seed Defaults
          </Button>
        </Group>
      </Group>

      {seedMutation.isSuccess && (
        <Alert icon={<IconCircleCheck size={16} />} color="green" variant="light">
          {seedMutation.data?.message}
        </Alert>
      )}

      {(createMutation.isError || deleteMutation.isError) && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {(createMutation.error as any)?.response?.data?.message ??
           (deleteMutation.error as any)?.response?.data?.message ??
           'An error occurred.'}
        </Alert>
      )}

      {isLoading ? (
        <Center py="xl"><Loader size="lg" /></Center>
      ) : (
        <Paper shadow="xs" radius="md" withBorder>
          <Accordion multiple chevronPosition="left">
            {categories.map((cat) => (
              <Accordion.Item key={cat.id} value={String(cat.id)}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap" pr="md">
                    <Group gap="sm">
                      <span style={{ fontSize: 18, lineHeight: 1, width: 20, textAlign: 'center' }}>
                        {cat.emoji ?? CATEGORY_EMOJI[cat.name] ?? '🗂️'}
                      </span>
                      <Text fw={600}>{cat.name}</Text>
                      <Badge size="sm" variant="light" color="gray">
                        {cat.subcategories.length} sub
                      </Badge>
                      {cat.productCount > 0 && (
                        <Badge size="sm" variant="light" color="blue">
                          {cat.productCount} products
                        </Badge>
                      )}
                    </Group>
                    <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                      <Badge color={cat.isActive ? 'green' : 'gray'} variant="light" size="sm">
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Tooltip label="Add subcategory">
                        <ActionIcon variant="subtle" size="sm" onClick={() => openAddModal(cat.id)}>
                          <IconPlus size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Edit">
                        <ActionIcon variant="subtle" size="sm" onClick={() => openEditModal(cat)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={cat.isActive ? 'Deactivate' : 'Activate'}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color={cat.isActive ? 'orange' : 'green'}
                          loading={toggleMutation.isPending}
                          onClick={() => toggleMutation.mutate(cat.id)}
                        >
                          {cat.isActive ? <IconToggleRight size={14} /> : <IconToggleLeft size={14} />}
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color="red"
                          loading={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(cat.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack gap="xs" pl="md">
                    {cat.subcategories.length === 0 ? (
                      <Text size="sm" c="dimmed">No subcategories yet.</Text>
                    ) : (
                      cat.subcategories.map((sub) => (
                        <Group key={sub.id} justify="space-between" py={4}>
                          <Group gap="sm">
                            <span style={{ fontSize: 16, lineHeight: 1, width: 18, textAlign: 'center' }}>
                              {sub.emoji ?? CATEGORY_EMOJI[sub.name] ?? '📦'}
                            </span>
                            <Text size="sm">{sub.name}</Text>
                            {sub.productCount > 0 && (
                              <Badge size="xs" variant="light" color="blue">
                                {sub.productCount} products
                              </Badge>
                            )}
                          </Group>
                          <Group gap="xs">
                            <Badge color={sub.isActive ? 'green' : 'gray'} variant="light" size="xs">
                              {sub.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                            <ActionIcon variant="subtle" size="xs" onClick={() => openEditModal(sub)}>
                              <IconPencil size={12} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              size="xs"
                              color={sub.isActive ? 'orange' : 'green'}
                              onClick={() => toggleMutation.mutate(sub.id)}
                            >
                              {sub.isActive ? <IconToggleRight size={12} /> : <IconToggleLeft size={12} />}
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              size="xs"
                              color="red"
                              onClick={() => deleteMutation.mutate(sub.id)}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      ))
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Paper>
      )}

      {/* Add Modal */}
      <Modal
        opened={addModal}
        onClose={closeAdd}
        title={selectedParentId ? 'Add Subcategory' : 'Add Category'}
      >
        <Stack gap="md">
          {selectedParentId && (
            <Text size="sm" c="dimmed">
              Parent: {categories.find(c => c.id === selectedParentId)?.name}
            </Text>
          )}
          <TextInput
            label="Name"
            placeholder={selectedParentId ? 'e.g. Apple iPhones' : 'e.g. Appliances'}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && newName.trim() && createMutation.mutate()}
          />
          <Group align="flex-end" gap="sm">
            <TextInput
              label="Emoji (optional)"
              placeholder="e.g. 🍎"
              value={newEmoji}
              onChange={(e) => setNewEmoji(e.target.value)}
              style={{ flex: 1 }}
              maxLength={10}
            />
            <EmojiPickerPopover value={newEmoji} onChange={setNewEmoji} />
          </Group>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeAdd}>Cancel</Button>
            <Button
              loading={createMutation.isPending}
              disabled={!newName.trim()}
              onClick={() => createMutation.mutate()}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Modal */}
      <Modal opened={editModal} onClose={closeEdit} title="Edit Category">
        <Stack gap="md">
          <TextInput
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && editName.trim() && updateMutation.mutate()}
          />
          <Group align="flex-end" gap="sm">
            <TextInput
              label="Emoji (optional)"
              placeholder="e.g. 🍎"
              value={editEmoji}
              onChange={(e) => setEditEmoji(e.target.value)}
              style={{ flex: 1 }}
              maxLength={10}
            />
            <EmojiPickerPopover value={editEmoji} onChange={setEditEmoji} />
          </Group>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeEdit}>Cancel</Button>
            <Button
              loading={updateMutation.isPending}
              disabled={!editName.trim()}
              onClick={() => updateMutation.mutate()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
